import React, {useMemo, useState} from 'react';
import {Container, Card, Form, Button} from 'react-bootstrap';
import {useFormik} from 'formik';
import * as Yup from 'yup';
import {Link, useNavigate} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'src/redux/storeHooks';
import {activateRoleUserAccountAsync} from 'src/redux/actions/authActions';
import useAppToast from 'src/hooks/useAppToast';
import 'bootstrap-icons/font/bootstrap-icons.css';

const passwordSchema = Yup.object().shape({
  password: Yup.string()
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])[a-zA-Z\d!@#$%^&*(),.?":{}|<>]{8,}$/,
      'Use 8 or more characters with at least one uppercase letter, one lowercase letter, one special character, and one number'
    )
    .required('Password is required'),
  confirm_password: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required('Confirm password is required'),
});

const SetPasswordPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {forgetPasswordToken, forgetPasswordTokenLoading} = useAppSelector((state) => state.auth);
  const activationToken = forgetPasswordToken;
  const {successToast, errorToast} = useAppToast();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const formik = useFormik({
    initialValues: {
      password: '',
      confirm_password: '',
    },
    validationSchema: passwordSchema,
    onSubmit: async (values, {resetForm, setSubmitting}) => {
      if (!activationToken) {
        errorToast('Activation token is missing');
        setSubmitting(false);
        return;
      }

      try {
        const response = await dispatch(
          activateRoleUserAccountAsync({
            activation_token: activationToken,
            password: values.password,
            confirm_password: values.confirm_password,
          })
        );

        const payload = response?.payload;

        if (payload?.status_code === 200) {
          successToast(payload?.massage);
          resetForm();
          setTimeout(() => {
            navigate('/login');
          }, 1000);
        } else {
          errorToast(payload?.massage || 'Something went wrong');
        }
      } catch (error) {
        errorToast('Unable to set password. Please try again later.');
      } finally {
        setSubmitting(false);
      }
    },
  });

  const passwordChecks = useMemo(() => {
    const value = formik.values.password || '';
    return {
      length: value.length >= 8,
      lowercase: /[a-z]/.test(value),
      uppercase: /[A-Z]/.test(value),
      number: /\d/.test(value),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(value),
    };
  }, [formik.values.password]);

  const requirementItems = useMemo(
    () => [
      {key: 'length', label: '8 characters minimum', satisfied: passwordChecks.length},
      {key: 'lowercase', label: 'A lowercase letter (a-z)', satisfied: passwordChecks.lowercase},
      {key: 'uppercase', label: 'An uppercase letter (A-Z)', satisfied: passwordChecks.uppercase},
      {key: 'number', label: 'A number (0-9)', satisfied: passwordChecks.number},
      {key: 'special', label: 'A special character (!@#$...)', satisfied: passwordChecks.special},
    ],
    [passwordChecks]
  );

  const satisfiedCount = requirementItems.filter((item) => item.satisfied).length;
  const strengthLevels = useMemo(
    () => [
      {label: 'Very Weak', color: '#e74c3c'},
      {label: 'Weak', color: '#e67e22'},
      {label: 'Fair', color: '#f1c40f'},
      {label: 'Strong', color: '#2ecc71'},
      {label: 'Very Strong', color: '#27ae60'},
    ],
    []
  );

  const activeStrengthIndex = formik.values.password
    ? Math.min(satisfiedCount, strengthLevels.length - 1)
    : 0;
  const activeStrength = strengthLevels[activeStrengthIndex];
  const strengthPercentage = formik.values.password
    ? Math.max((satisfiedCount / requirementItems.length) * 100, 15)
    : 10;

  return (
    <Container className='py-5 d-flex justify-content-center align-items-center'>
      <style>
        {`
          .floating-label-group {
            position: relative;
            margin-bottom: 1.5rem;
          }

          .floating-label-group input {
            border: 1px solid #ced4da;
            border-radius: 0.5rem;
            padding: 1rem 0.75rem 1rem 0.75rem;
            font-size: 1rem;
            width: 100%;
            transition: border-color 0.3s, padding-top 0.3s;
            background: none;
          }

          .floating-label-group--with-toggle input {
            padding-right: 3rem;
          }

          .floating-label-group label {
            position: absolute;
            top: 1rem;
            left: 0.75rem;
            font-size: 1rem;
            color: #6c757d;
            transition: 0.2s ease all;
            pointer-events: none;
            background: white;
            padding: 0 0.5rem;
            transform-origin: left top;
          }

          .password-toggle-btn {
            position: absolute;
            top: 50%;
            right: 1.75rem;
            transform: translateY(-50%);
            border: none;
            background: transparent;
            color: #6c757d;
            padding: 0;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .password-toggle-btn:hover,
          .password-toggle-btn:focus {
            color: #0d6efd;
            outline: none;
          }

          .password-toggle-btn i {
            font-size: 1.1rem;
          }

          .floating-label-group input:focus {
            outline: none;
            border-color: #0d6efd;
          }

          .floating-label-group input:focus + label,
          .floating-label-group input:not(:placeholder-shown) + label {
            top: -0.7rem;
            left: 0.6rem;
            font-size: 0.8rem;
            color: #0d6efd;
            padding: 0 0.25rem;
          }

          .password-strength {
            border-radius: 0.75rem;
            background-color: #f8f9fa;
            padding: 0.75rem 1rem;
            border: 1px solid #e9ecef;
          }

          .password-strength__header {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-weight: 600;
            color: #344054;
            margin-bottom: 0.5rem;
          }

          .password-strength__title {
            font-weight: 600;
          }

          .password-strength__value {
            font-weight: 700;
          }

          .password-strength__bar {
            height: 8px;
            width: 100%;
            border-radius: 999px;
            background-color: #e9ecef;
            overflow: hidden;
          }

          .password-strength__bar-fill {
            height: 100%;
            border-radius: 999px;
            transition: width 0.3s ease;
          }

          .password-requirements {
            background-color: #f5f8ff;
            border-radius: 0.75rem;
            border: 1px solid #dbe4ff;
            padding: 1rem 1.25rem;
          }

          .password-requirements__title {
            font-weight: 600;
            color: #1f3c88;
            margin-bottom: 0.75rem;
          }

          .password-requirements__grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
            gap: 0.6rem 1.25rem;
          }

          .password-requirements__item {
            display: flex;
            align-items: center;
            gap: 0.6rem;
            color: #4b5875;
            font-size: 0.92rem;
          }

          .password-requirements__item--met {
            color: #198754;
            font-weight: 600;
          }

          .password-requirements__indicator {
            width: 0.75rem;
            height: 0.75rem;
            border-radius: 50%;
            border: 2px solid #dc3545;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            position: relative;
          }

          .password-requirements__indicator::after {
            content: '';
            width: 0.35rem;
            height: 0.35rem;
            border-radius: 50%;
            background-color: #dc3545;
          }

          .password-requirements__indicator--met {
            border-color: #198754;
          }

          .password-requirements__indicator--met::after {
            background-color: #198754;
          }

          .card-style {
            background-color: #ffffff;
            border-radius: 1rem;
            box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
            width: 100%;
            max-width: 500px;
          }

          @media (max-width: 576px) {
            .card-style {
              padding: 1.5rem !important;
              border-radius: 0.75rem;
            }
          }
        `}
      </style>

      <Card className='p-4 card-style'>
        {forgetPasswordTokenLoading ? (
          <div className='text-center mb-4'>
            <span className='spinner-border spinner-border-sm'></span>
            <span className='ms-2'>Loading token...</span>
          </div>
        ) : (
          <>
            <div className='text-center mb-3'>
              <h4 className='fw-bold mb-2'>Set Password</h4>
              <p className='text-muted'>Enter your new password below to set up your account.</p>
            </div>

            <Form onSubmit={formik.handleSubmit}>
              <div className='floating-label-group floating-label-group--with-toggle'>
                <Form.Control
                  type={showPassword ? 'text' : 'password'}
                  placeholder=' '
                  required
                  id='password'
                  {...formik.getFieldProps('password')}
                  isInvalid={formik.touched.password && !!formik.errors.password}
                />
                <Form.Label>Password</Form.Label>
                {formik.touched.password && formik.errors.password && (
                  <div className='invalid-feedback'>{formik.errors.password}</div>
                )}
                <button
                  type='button'
                  className='password-toggle-btn'
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <i className={`bi ${showPassword ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`} />
                </button>
              </div>

              <div className='floating-label-group floating-label-group--with-toggle'>
                <Form.Control
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder=' '
                  required
                  id='confirm_password'
                  {...formik.getFieldProps('confirm_password')}
                  isInvalid={formik.touched.confirm_password && !!formik.errors.confirm_password}
                />
                <Form.Label>Confirm Password</Form.Label>
                {formik.touched.confirm_password && formik.errors.confirm_password && (
                  <div className='invalid-feedback'>{formik.errors.confirm_password}</div>
                )}
                <button
                  type='button'
                  className='password-toggle-btn'
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  aria-label={
                    showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'
                  }
                >
                  <i
                    className={`bi ${showConfirmPassword ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}
                  />
                </button>
              </div>

              <div className='password-strength mb-3'>
                <div className='password-strength__header'>
                  <span className='password-strength__title'>Password Strength:</span>
                  <span className='password-strength__value' style={{color: activeStrength.color}}>
                    {activeStrength.label}
                  </span>
                </div>
                <div className='password-strength__bar'>
                  <div
                    className='password-strength__bar-fill'
                    style={{
                      width: `${strengthPercentage}%`,
                      backgroundColor: activeStrength.color,
                    }}
                  />
                </div>
              </div>

              <div className='password-requirements mb-4'>
                <p className='password-requirements__title'>Your password must include:</p>
                <div className='password-requirements__grid'>
                  {requirementItems.map((item) => (
                    <div
                      key={item.key}
                      className={`password-requirements__item ${
                        item.satisfied ? 'password-requirements__item--met' : ''
                      }`}
                    >
                      <span
                        className={`password-requirements__indicator ${
                          item.satisfied ? 'password-requirements__indicator--met' : ''
                        }`}
                      />
                      <span>{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Button
                type='submit'
                variant='primary'
                className='w-100 mb-3'
                disabled={formik.isSubmitting}
              >
                {formik.isSubmitting ? 'Saving...' : 'Set Password'}
              </Button>

              <div className='text-center'>
                <Link to='/auth/login-page' className='text-decoration-none'>
                  Back to Login
                </Link>
              </div>
            </Form>
          </>
        )}
      </Card>
    </Container>
  )
};

export default SetPasswordPage;
